import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * Changing who is on a saved list, and downloading it — fix round R, customer-portal review CL #121.
 *
 * Before this panel the list page said "upload the corrected file as a new list": a saved list's
 * people could not be added to, replaced or downloaded. Now an Owner or Admin can
 *
 *   · **add people** from a CSV or a paste — read exactly as a new list is (the same inspection,
 *     the same column mapping, the same refusals); numbers already on the list are skipped;
 *   · **replace everyone** with a new file's people — the name and the rules stay;
 *   · **download** the list as CSV (name, number, language, time zone, details, do not call).
 *
 * The API refuses the first two while a campaign uses the list (a campaign keeps the list it
 * called); the panel says so up front rather than offering a button that will be refused. People
 * with no time zone get the list's own choice ({@link ContactListDetailView.assumedTimeZone}).
 *
 * AMENDED 2026-10-10 (design sync, the export of 2026-10-10): the people are read under the new list's FIXED FORMAT
 * — `phone, name, language, note` named in the first row — with the same format panel, "Columns found" and paste
 * check, so a file that makes a list also adds to one.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Segmented, Textarea, type SegmentedOption } from "@wetalk/ui";
import { useId, useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import type { ContactListDetailView } from "../../api/gateway.js";
import { useMemberCan } from "../../shell/member-capability.js";

import { countText, contactListFileName, emptySourceText, pasteHeaderOf, peopleChangedText, refusalOf } from "./contact-list-copy.js";
import { NOT_UTF8_MESSAGE, readContactFile } from "./contact-list-file.js";
import { InspectionResult, ListFormat, PasteCheck, useFixedInspection } from "./new-contact-list-panel.js";

type ChangeKind = "add" | "replace";
type WorkingSource = "csv_upload" | "paste";

const KIND_OPTION: readonly SegmentedOption<ChangeKind>[] = [
  { value: "add", get label() { return portalText("operations", "add_people_b9c735ea"); } },
  { value: "replace", get label() { return portalText("operations", "replace_everyone_e1e8bcc3"); } },
];

const SOURCE_OPTION: readonly SegmentedOption<WorkingSource>[] = [
  { value: "csv_upload", get label() { return portalText("operations", "upload_a_csv_cb85d3b6"); } },
  { value: "paste", get label() { return portalText("operations", "type_or_paste_4f4ffe2f"); } },
];

/** Its own key, apart from the new-list panel's and the lists' (CL #136). */
const INSPECTION_KEY = "contact-list-people-inspection";

/** Saves text as a file in the browser: the API's CSV, under the name the API gives it. */
function saveText(text: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function ChangePeoplePanel({ detail }: { readonly detail: ContactListDetailView }): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const member = useMemberCan();

  const [kind, setKind] = useState<ChangeKind>("add");
  const [source, setSource] = useState<WorkingSource>("csv_upload");
  const [fileName, setFileName] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileNotice, setFileNotice] = usePortalMessage();
  const [pasteText, setPasteText] = useState("");
  const [done, setDone] = usePortalMessage();
  const formatId = useId();
  const pasteCheckId = useId();

  const sourceHandle = source === "csv_upload" ? fileText : pasteText;
  const pasteHeader = source === "paste" ? pasteHeaderOf(pasteText) : null;
  const attached = sourceHandle.trim() !== "" && (source === "csv_upload" || pasteHeader?.named === true);
  const { inspection, column, counted } = useFixedInspection(gateway, INSPECTION_KEY, source, sourceHandle, attached);

  const change = useMutation({
    mutationFn: () => {
      const draft = { source, sourceHandle, column, assumedTimeZone: detail.assumedTimeZone };
      return kind === "add"
        ? gateway.addContactListPeople(detail.contactListId, draft)
        : gateway.replaceContactListPeople(detail.contactListId, draft);
    },
    onSuccess: async (result) => {
      setDone(() => peopleChangedText(kind, result));
      setFileName("");
      setFileText("");
      setPasteText("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
    },
  });

  const download = useMutation({
    mutationFn: () => gateway.downloadContactList(detail.contactListId),
    onSuccess: (text) => {
      saveText(text, contactListFileName(detail.name, new Date().toISOString().slice(0, 10)));
    },
  });

  const touch = (): void => {
    setDone(null);
    if (change.isError) change.reset();
  };

  const refusedWhy = member.whyNot("contact_list.people");
  if (refusedWhy !== null) {
    return (
      <div className="cl-detail-section">
        <h2 className="cl-section-title">{portalText("operations", "change_who_is_on_this_list_59f3bdb6")}</h2>
        <p className="c-note">{refusedWhy}</p>
      </div>
    );
  }

  const readable = counted?.readableCount ?? 0;
  const ready = attached && counted !== undefined && counted.rowCount > 0 && column.some((one) => one.role === "e164") && readable > 0;

  return (
    <div className="cl-detail-section">
      <h2 className="cl-section-title">{portalText("operations", "change_who_is_on_this_list_59f3bdb6")}</h2>
      <div className="c-row cl-detail-actions">
        <Button
          variant="secondary"
          disabled={download.isPending}
          onClick={() => {
            download.mutate();
          }}
        >
          {download.isPending ? portalText("operations", "preparing_the_file_66cc970a") : portalText("operations", "download_as_csv_e989d9f0")}
        </Button>
      </div>
      {download.error === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {portalText("shell", "merge.contacts.downloadFailed")} {refusalOf(download.error).message}
        </p>
      )}

      {detail.campaignCount > 0 ? (
        <p className="c-note">{portalText("operations", "a_campaign_uses_this_list_so_the_people_on_it_cannot_be_ch_354e22e8")}{" "}</p>
      ) : (
        <form
          className="c-panel cl-detail-panel"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready && !change.isPending) change.mutate();
          }}
        >
          <Segmented
            name="changePeopleKind"
            legend={portalText("operations", "what_to_do_779b1d58")}
            value={kind}
            options={KIND_OPTION}
            onChange={(next) => {
              touch();
              setKind(next);
            }}
          />
          <p className="c-drop-hint">
            {kind === "add"
              ? portalText("operations", "the_people_in_the_file_join_the_list_numbers_already_on_it_ada50f89")
              : portalText("shell", "merge.contacts.replacePeople", { count: countText(detail.personCount) })}
            {detail.assumedTimeZone === null
              ? ""
              : portalText("shell", "merge.contacts.assumedZone", { zone: detail.assumedTimeZone })}
          </p>
          <Segmented
            name="changePeopleSource"
            legend={portalText("operations", "where_they_come_from_5ea5fa3f")}
            value={source}
            options={SOURCE_OPTION}
            onChange={(next) => {
              touch();
              setSource(next);
            }}
          />
          <ListFormat id={formatId} />
          {source === "csv_upload" ? (
            <label className="c-drop cl-drop" data-attached={attached}>
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                className="c-visually-hidden"
                aria-label={portalText("operations", "choose_a_csv_d7765b6b")}
                onChange={(event) => {
                  const chosen = event.currentTarget.files?.[0];
                  // Cleared so the same file, saved again after a refusal, is read again.
                  event.currentTarget.value = "";
                  touch();
                  setFileText("");
                  setFileNotice(null);
                  setFileName(chosen === undefined ? "" : chosen.name);
                  if (chosen === undefined) return;
                  readContactFile(chosen).then(
                    (reading) => {
                      if (reading.kind === "text") setFileText(reading.text);
                      else if (reading.kind === "refused") setFileNotice(reading.message);
                      else setFileNotice(() => portalText("shell", "merge.contacts.badEncoding", { message: NOT_UTF8_MESSAGE() }));
                    },
                    () => {
                      setFileNotice(portalText("operations", "that_file_could_not_be_read_by_the_browser_so_nothing_was_9b047bd6"));
                    },
                  );
                }}
              />
              <span className="c-drop-name">{fileName !== "" ? fileName : portalText("operations", "choose_a_csv_d7765b6b")}</span>
              <span className="c-drop-hint">{portalText("shell", "merge.contacts.dropHint")}</span>
            </label>
          ) : (
            <div className="cl-paste-field">
            <Field label={portalText("shell", "merge.contacts.pasteLabel")}>
              {(control) => (
                <>
                  <Textarea
                    {...control}
                    value={pasteText}
                    rows={4}
                    aria-describedby={`${formatId} ${pasteCheckId}`}
                    onChange={(event) => {
                      const text = event.currentTarget.value;
                      touch();
                      setPasteText(text);
                    }}
                  />
                  <PasteCheck id={pasteCheckId} text={pasteText} counted={attached ? counted : undefined} />
                </>
              )}
            </Field>
            </div>
          )}
          {fileNotice === null ? null : (
            <p className="c-note c-note-critical" role="alert">
              {fileNotice}
            </p>
          )}
          {attached && inspection.isError ? (
            <p className="c-note c-note-critical" role="alert">
              {refusalOf(inspection.error).message}
            </p>
          ) : null}
          {attached && inspection.data === undefined && !inspection.isError ? (
            <p className="c-note">{portalText("operations", "reading_what_is_in_it_cfde9ca5")}</p>
          ) : null}
          {!attached || inspection.data === undefined ? null : inspection.data.rowCount === 0 ? (
            <p className="c-note c-note-critical" role="alert">
              {emptySourceText(source)}
            </p>
          ) : source === "paste" ? null : (
            <InspectionResult column={column} counted={counted} columnRefusal={null} />
          )}
          {change.error === null ? null : (
            <p className="c-note c-note-critical" role="alert">
              {refusalOf(change.error).message}
            </p>
          )}
          <div className="c-row">
            <Button variant="primary" type="submit" disabled={!ready || change.isPending}>
              {change.isPending
                ? portalText("operations", "saving_56a2285c")
                : kind === "add"
                  ? portalText("operations", "add_these_people_d0710a94")
                  : portalText("operations", "replace_everyone_on_the_list_0b9b0107")}
            </Button>
            {done === null ? null : (
              <span className="c-note" role="status">
                {done}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
